/**
 * node-content.ts — innhold for /nodejs (norsk + engelsk)
 *
 * ⚠ PLASSHOLDERE: pakker, priser, antall apper, RAM/CPU, lagring, trafikk, Node-versjoner,
 * Git-deploy, zero-downtime, WebSockets, databaser, logger, FAQ og kontaktadresse er
 * foreløpige og MÅ erstattes med det som faktisk leveres før siden publiseres. Teknisk
 * spesifikasjon avgjør valget for utviklere, så alt må leses gjennom av eier.
 * All redigering gjøres her — komponentene har ingen egne tekster eller tall.
 *
 * VAREMERKE: «Node.js» er et varemerke tilhørende OpenJS Foundation, og navnet inNode
 * bruker «Node». Teksten sier «Node.js-hosting» som beskrivelse. Footer og FAQ har en
 * avklaring om at inSuite ikke er tilknyttet. Få navnebruken bekreftet (OpenJS Foundation
 * har egne retningslinjer for bruk av merket) før publisering.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Når prisene skal styres fra
 * systemet, erstattes denne filen av et API-kall (NodeContent i src/types/node.ts er
 * kontrakten).
 */

import type { Locale } from '@/types/landing';
import type { NodeContent } from '@/types/node';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
const CONTACT = 'mailto:admin@ntune.no?subject=inNode';

const no: NodeContent = {
  seoTitle: 'inNode — Node.js-hosting fra inSuite',
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }],
    current: 'inNode',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'pakker', label: 'Pakker' },
      { id: 'sammenlign', label: 'Sammenlign' },
      { id: 'kom-i-gang', label: 'Kom i gang' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se pakkene', href: '#pakker' },
  },
  hero: {
    heading: 'inNode',
    subheading:
      'Node.js-hosting der du deployer fra Git, velger Node-versjon og lar oss ta driften, i tre størrelser du kan bytte mellom når appene dine vokser.',
    primaryCta: { label: 'Se pakker og priser', href: '#pakker' },
    secondaryCta: { label: 'Dette får du', href: '#funksjoner' },
    illustrationLabel: 'Illustrasjon av et kodevindu, en deploy-kjede og en server',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Fra git push til live.',
    body: [
      'inNode er hosting for Node.js-applikasjoner. Du leverer koden, og vi kjører den: med riktig Node-versjon, automatisk omstart og SSL.',
      'Trenger du full kontroll over selve serveren? Da passer inVPS bedre.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Det som skal til for å kjøre en Node.js-applikasjon i produksjon.',
    panelLabel: 'Illustrasjon av et kodevindu og en server',
    items: [
      { title: 'Deploy fra Git', body: 'Koble repoet ditt, og push for å deploye. Vi bygger og starter appen.' },
      { title: 'Velg Node-versjon', body: 'Kjør appen på den Node.js-versjonen du trenger, og bytt når du er klar.' },
      { title: 'Automatisk omstart', body: 'Appen startes på nytt hvis den stopper, og følger med serveren ved oppdateringer.' },
      { title: 'Miljøvariabler', body: 'Lagre konfigurasjon og hemmeligheter utenfor koden, per miljø.' },
      { title: 'Domener og SSL', body: 'Egne domener med SSL-sertifikat som fornyes automatisk.' },
      { title: 'Logger og overvåking', body: 'Se loggene fra appen, og følg med på minne og CPU.' },
      { title: 'WebSockets', body: 'Sanntidsforbindelser og lange tilkoblinger fungerer uten ekstra oppsett.' },
    ],
  },
  promo: {
    title: 'Trenger du full kontroll?',
    body: 'Vil du styre serveren selv, med rotilgang og eget operativsystem, er inVPS et bedre valg.',
    cta: { label: 'Se inVPS', href: '/vps' },
  },
  plans: {
    eyebrow: 'Pakker',
    heading: 'Pakker og priser',
    intro: 'Velg pakken som passer, og gå opp en størrelse når appene dine vokser.',
    cardsHeading: 'Velg pakke',
    fromLabel: 'Fra',
    footnote: '*Prisene er veiledende, per måned og eksklusiv mva.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For en enkel app eller API.',
        price: '99',
        priceNote: 'kr / mnd',
        features: ['1 app', '512 MB RAM', '5 GB lagring', '100 GB trafikk', 'SSL-sertifikat'],
        cta: { label: 'Bestill Start', href: CONTACT },
      },
      {
        id: 'vekst',
        name: 'Vekst',
        summary: 'For apper i produksjon.',
        price: '249',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Start, pluss',
        features: ['3 apper', '2 GB RAM', '20 GB lagring', '500 GB trafikk', 'Deploy uten nedetid'],
        cta: { label: 'Bestill Vekst', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Anbefalt',
      },
      {
        id: 'pro',
        name: 'Pro',
        summary: 'For krevende arbeidslaster.',
        price: '599',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i Vekst, pluss',
        features: ['10 apper', '8 GB RAM', '80 GB lagring', '2 TB trafikk', 'Prioritert support'],
        cta: { label: 'Bestill Pro', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Sammenlign',
    heading: 'Sammenlign pakkene',
    intro: 'Se hva hver pakke inneholder.',
    featureLabel: 'Funksjon',
    yes: 'Inkludert',
    no: 'Ikke inkludert',
    rows: [
      { label: 'Ressurser', values: [], group: true },
      { label: 'Apper', values: ['1', '3', '10'] },
      { label: 'RAM', values: ['512 MB', '2 GB', '8 GB'] },
      { label: 'Lagring', values: ['5 GB', '20 GB', '80 GB'] },
      { label: 'Trafikk per måned', values: ['100 GB', '500 GB', '2 TB'] },
      { label: 'Funksjoner og support', values: [], group: true },
      { label: 'Deploy fra Git', values: [true, true, true] },
      { label: 'SSL-sertifikat', values: [true, true, true] },
      { label: 'WebSockets', values: [true, true, true] },
      { label: 'Deploy uten nedetid', values: [false, true, true] },
      { label: 'Prioritert support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Kom i gang',
    heading: 'Fra repo til live',
    intro: 'Fire steg, og du trenger ikke sette opp noen server.',
    panelLabel: 'Illustrasjon av et kodevindu og en server',
    items: [
      { title: 'Velg pakke', body: 'Velg størrelsen som passer antall apper og ressursbehov.' },
      { title: 'Koble Git-repoet', body: 'Gi oss tilgang til repoet, og velg Node-versjon og startkommando.' },
      { title: 'Vi bygger og starter appen', body: 'Vi bygger koden og starter appen med SSL og automatisk omstart.' },
      { title: 'Pek domenet og publiser', body: 'Du peker domenet mot oss, og appen er live. Nye deploys skjer ved git push.' },
    ],
  },
  useCases: {
    eyebrow: 'Bruksområder',
    heading: 'Slik brukes det',
    items: [
      {
        icon: 'api',
        title: 'API-er og backends',
        body: 'Kjør REST- og GraphQL-API-er som frontend og apper kan bruke.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'code',
        title: 'Webapplikasjoner',
        body: 'Kjør applikasjoner bygget med rammeverk som Express og Next.js.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'process',
        title: 'Sanntid og bakgrunnsjobber',
        body: 'Chat, varsler og planlagte jobber som krever en prosess som kjører hele tiden.',
        link: { label: 'Kontakt oss', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Relatert',
    heading: 'Relaterte tjenester',
    items: [
      { icon: 'virtual', name: 'inVPS — virtuelle servere', href: '/vps' },
      { icon: 'chip', name: 'inGPU — GPU-deploy', href: '/gpu' },
      { icon: 'server', name: 'inHost — webhotell', href: '/webhotell' },
      { icon: 'wordpress', name: 'inWordPress — WordPress-hosting', href: '/wordpress' },
      { icon: 'globe', name: 'inDomain — domener', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      {
        question: 'Hva er forskjellen på inNode og inVPS?',
        answer:
          'Med inNode leverer du koden, og vi står for serveren, Node-versjonen og driften. Med inVPS får du en egen virtuell server og styrer alt selv, med rotilgang.',
      },
      { question: 'Hvilke Node-versjoner støttes?', answer: 'Vi støtter flere Node.js-versjoner (LTS). Ta kontakt for oversikt.' },
      { question: 'Kan jeg bruke en database?', answer: 'Ja, du kan bruke en database sammen med appen. Ta kontakt for hvilke vi støtter.' },
      { question: 'Hvordan deployer jeg?', answer: 'Du kobler Git-repoet, og deployer ved å pushe. Vi bygger og starter appen.' },
      { question: 'Hvordan bestiller jeg?', answer: 'Send oss en e-post med pakken du ønsker, så tar vi kontakt for å sette opp appen din.' },
      {
        question: 'Er inSuite tilknyttet Node.js?',
        answer: 'Nei. Node.js er et varemerke tilhørende OpenJS Foundation. inSuite er ikke tilknyttet eller godkjent av OpenJS Foundation.',
      },
    ],
  },
  cta: {
    heading: 'Klar til å deploye?',
    body: 'Kontakt oss, så setter vi opp appen din.',
    primary: { label: 'Kontakt oss', href: CONTACT },
  },
  footer: {
    tagline: 'inNode — Node.js-hosting der du leverer koden og vi tar driften.',
    legal: 'inSuite. Node.js er et varemerke tilhørende OpenJS Foundation; inSuite er ikke tilknyttet.',
    platformLabel: 'inSuite',
    sectionsLabel: 'inNode',
  },
};

const en: NodeContent = {
  seoTitle: 'inNode — Node.js hosting from inSuite',
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }],
    current: 'inNode',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'pakker', label: 'Plans' },
      { id: 'sammenlign', label: 'Compare' },
      { id: 'kom-i-gang', label: 'Get started' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the plans', href: '#pakker' },
  },
  hero: {
    heading: 'inNode',
    subheading:
      'Node.js hosting where you deploy from Git, pick your Node version and let us run the operations, in three sizes you can move between as your apps grow.',
    primaryCta: { label: 'See plans and pricing', href: '#pakker' },
    secondaryCta: { label: 'What you get', href: '#funksjoner' },
    illustrationLabel: 'Illustration of a code window, a deploy chain and a server',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'From git push to live.',
    body: [
      'inNode is hosting for Node.js applications. You deliver the code, and we run it: with the right Node version, automatic restarts and SSL.',
      'Do you need full control over the server itself? Then inVPS is a better fit.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'What it takes to run a Node.js application in production.',
    panelLabel: 'Illustration of a code window and a server',
    items: [
      { title: 'Deploy from Git', body: 'Connect your repository, and push to deploy. We build and start the app.' },
      { title: 'Choose your Node version', body: 'Run the app on the Node.js version you need, and switch when you are ready.' },
      { title: 'Automatic restarts', body: 'The app is restarted if it stops, and follows the server through updates.' },
      { title: 'Environment variables', body: 'Store configuration and secrets outside the code, per environment.' },
      { title: 'Domains and SSL', body: 'Your own domains with an SSL certificate that renews automatically.' },
      { title: 'Logs and monitoring', body: 'See the app’s logs, and keep an eye on memory and CPU.' },
      { title: 'WebSockets', body: 'Real-time connections and long-lived connections work without extra setup.' },
    ],
  },
  promo: {
    title: 'Need full control?',
    body: 'If you want to manage the server yourself, with root access and your own operating system, inVPS is a better choice.',
    cta: { label: 'See inVPS', href: '/vps' },
  },
  plans: {
    eyebrow: 'Plans',
    heading: 'Plans and pricing',
    intro: 'Pick the plan that fits, and move up a size as your apps grow.',
    cardsHeading: 'Choose a plan',
    fromLabel: 'From',
    footnote: '*Prices are indicative, per month and exclude VAT.',
    items: [
      {
        id: 'start',
        name: 'Start',
        summary: 'For a simple app or API.',
        price: '99',
        priceNote: 'NOK / month',
        features: ['1 app', '512 MB RAM', '5 GB storage', '100 GB traffic', 'SSL certificate'],
        cta: { label: 'Order Start', href: CONTACT },
      },
      {
        id: 'vekst',
        name: 'Growth',
        summary: 'For apps in production.',
        price: '249',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Start, plus',
        features: ['3 apps', '2 GB RAM', '20 GB storage', '500 GB traffic', 'Zero-downtime deploys'],
        cta: { label: 'Order Growth', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Recommended',
      },
      {
        id: 'pro',
        name: 'Pro',
        summary: 'For demanding workloads.',
        price: '599',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in Growth, plus',
        features: ['10 apps', '8 GB RAM', '80 GB storage', '2 TB traffic', 'Priority support'],
        cta: { label: 'Order Pro', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Compare',
    heading: 'Compare the plans',
    intro: 'See what each plan includes.',
    featureLabel: 'Feature',
    yes: 'Included',
    no: 'Not included',
    rows: [
      { label: 'Resources', values: [], group: true },
      { label: 'Apps', values: ['1', '3', '10'] },
      { label: 'RAM', values: ['512 MB', '2 GB', '8 GB'] },
      { label: 'Storage', values: ['5 GB', '20 GB', '80 GB'] },
      { label: 'Traffic per month', values: ['100 GB', '500 GB', '2 TB'] },
      { label: 'Features and support', values: [], group: true },
      { label: 'Deploy from Git', values: [true, true, true] },
      { label: 'SSL certificate', values: [true, true, true] },
      { label: 'WebSockets', values: [true, true, true] },
      { label: 'Zero-downtime deploys', values: [false, true, true] },
      { label: 'Priority support', values: [false, false, true] },
    ],
  },
  steps: {
    eyebrow: 'Get started',
    heading: 'From repo to live',
    intro: 'Four steps, and you do not have to set up any server.',
    panelLabel: 'Illustration of a code window and a server',
    items: [
      { title: 'Choose a plan', body: 'Pick the size that fits your number of apps and resource needs.' },
      { title: 'Connect your Git repository', body: 'Give us access to the repository, and choose the Node version and start command.' },
      { title: 'We build and start the app', body: 'We build the code and start the app with SSL and automatic restarts.' },
      { title: 'Point your domain and publish', body: 'You point your domain to us, and the app is live. New deploys happen on git push.' },
    ],
  },
  useCases: {
    eyebrow: 'Use cases',
    heading: 'How it is used',
    items: [
      {
        icon: 'api',
        title: 'APIs and backends',
        body: 'Run REST and GraphQL APIs that frontends and apps can use.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'code',
        title: 'Web applications',
        body: 'Run applications built with frameworks like Express and Next.js.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'process',
        title: 'Real-time and background jobs',
        body: 'Chat, notifications and scheduled jobs that need a process running all the time.',
        link: { label: 'Contact us', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Related',
    heading: 'Related services',
    items: [
      { icon: 'virtual', name: 'inVPS — virtual servers', href: '/vps' },
      { icon: 'chip', name: 'inGPU — GPU deploy', href: '/gpu' },
      { icon: 'server', name: 'inHost — web hosting', href: '/webhotell' },
      { icon: 'wordpress', name: 'inWordPress — WordPress hosting', href: '/wordpress' },
      { icon: 'globe', name: 'inDomain — domains', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'What is the difference between inNode and inVPS?',
        answer:
          'With inNode you deliver the code, and we handle the server, the Node version and the operations. With inVPS you get your own virtual server and manage everything yourself, with root access.',
      },
      { question: 'Which Node versions are supported?', answer: 'We support several Node.js versions (LTS). Get in touch for the list.' },
      { question: 'Can I use a database?', answer: 'Yes, you can use a database alongside the app. Get in touch for which ones we support.' },
      { question: 'How do I deploy?', answer: 'You connect your Git repository, and deploy by pushing. We build and start the app.' },
      { question: 'How do I order?', answer: 'Send us an email with the plan you want, and we will get in touch to set up your app.' },
      {
        question: 'Is inSuite affiliated with Node.js?',
        answer: 'No. Node.js is a trademark of the OpenJS Foundation. inSuite is not affiliated with or endorsed by the OpenJS Foundation.',
      },
    ],
  },
  cta: {
    heading: 'Ready to deploy?',
    body: 'Contact us and we will set up your app.',
    primary: { label: 'Contact us', href: CONTACT },
  },
  footer: {
    tagline: 'inNode — Node.js hosting where you deliver the code and we run the operations.',
    legal: 'inSuite. Node.js is a trademark of the OpenJS Foundation; inSuite is not affiliated.',
    platformLabel: 'inSuite',
    sectionsLabel: 'inNode',
  },
};

export const NODE_CONTENT: Record<Locale, NodeContent> = { no, en };
